import { NO_PERIOD_PERIOD_NAME, Periods } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { comparePeriods, getPreviousPeriod } from '@app/src/utils/getOrderedPeriods'

interface DeviationResult {
  deviation?: number
  previousAnswer: number
  previousAnswerPeriod: string
}

const findRelevantAnswerForPeriod = (
  periodName: string,
  previousAnswersToQuestion: ResponseItem[],
  questionnaireTemplateId: number | undefined,
): ResponseItem | undefined => {
  const matchingAnswers = previousAnswersToQuestion.filter(p => p.response.request.periodName === periodName)
  if (matchingAnswers.length === 0) return undefined
  if (matchingAnswers.length === 1) return matchingAnswers[0]

  const allAnswersAreTheSame = matchingAnswers.every(m => m.answer === matchingAnswers[0].answer)
  if (allAnswersAreTheSame) return matchingAnswers[0]

  const answerGivenInSameQuestionnaire = matchingAnswers.find(
    m => m?.response?.request?.questionnaireTemplateId === questionnaireTemplateId,
  )
  if (answerGivenInSameQuestionnaire) return answerGivenInSameQuestionnaire

  return undefined
}

export const getPreviousAnswerSummary = (
  responseItem: ResponseItem,
  previousAnswers: ResponseItem[],
): {
  period: string
  relevantAnswer: string | number
}[] => {
  const periodType = responseItem.response.request.periodType
  const previousAnswersSamePeriodType = previousAnswers.filter(
    p =>
      p.response.request.periodType === periodType &&
      p.response.request.periodType !== Periods.NoPeriod &&
      new Date(p.response.request.periodStartsAt ?? '') < new Date(responseItem.response.request.periodStartsAt ?? ''),
  )

  //TODO Use Object.groupBy when we upgraded our tests to use Node version >= 21
  const answersByPeriod = previousAnswersSamePeriodType.reduce<Record<string, ResponseItem[]>>((acc, item) => {
    const key = item.response.request.periodName
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(item)
    return acc
  }, {})

  return Object.entries(answersByPeriod)
    .flatMap(([period, items]) => {
      const relevantAnswer = findRelevantAnswerForPeriod(
        period,
        items ?? [],
        responseItem.response.request.questionnaireTemplateId,
      )
      if (!relevantAnswer) return []
      return [{ period, relevantAnswer: relevantAnswer.answer }]
    })
    .sort((a, b) => -comparePeriods(a.period, b.period))
}

export const calculateDeviation = (
  amount: string | number | undefined,
  previousAnswersToQuestion: ResponseItem[],
  currentPeriodName: string | undefined,
  questionnaireTemplateId: number | undefined,
): DeviationResult | undefined => {
  if (!amount || !currentPeriodName || currentPeriodName === NO_PERIOD_PERIOD_NAME) return undefined

  const currentAmountNumber = Number(amount)
  if (Number.isNaN(currentAmountNumber)) return undefined

  const previousPeriod = getPreviousPeriod(currentPeriodName)
  const answerFromLastPeriod = findRelevantAnswerForPeriod(
    previousPeriod,
    previousAnswersToQuestion,
    questionnaireTemplateId,
  )
  if (!answerFromLastPeriod) return undefined

  const lastPeriodAnswer = Number(answerFromLastPeriod?.answer)
  if (Number.isNaN(lastPeriodAnswer)) return undefined

  const deviation = lastPeriodAnswer === 0 ? undefined : Math.round((currentAmountNumber / lastPeriodAnswer - 1) * 100)
  return {
    deviation,
    previousAnswer: lastPeriodAnswer,
    previousAnswerPeriod: previousPeriod,
  }
}
